import { apiRequest } from "./client";
import { isApiEnabled } from "./config";

export type UiStringsPublicResponse = {
  locale: "ru" | "en";
  version: string;
  strings: Record<string, string>;
};

export type UiStringsAdminResponse = {
  version: string;
  overrides: Record<string, { ru?: string; en?: string; updatedAt?: string }>;
};

const CACHE_KEY = "mvk.ui_string_overrides";

type CachePayload = { version: string; byLocale: Record<string, Record<string, string>> };

function readCache(): CachePayload | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as CachePayload) : null;
  } catch {
    return null;
  }
}

function writeCache(payload: CachePayload) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

/** Unflatten `a.b.c` → nested object for i18n.addResourceBundle. */
export function unflattenStrings(flat: Record<string, string>): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  for (const [path, value] of Object.entries(flat)) {
    const parts = path.split(".");
    let cur: Record<string, unknown> = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i]!;
      if (!cur[p] || typeof cur[p] !== "object") cur[p] = {};
      cur = cur[p] as Record<string, unknown>;
    }
    cur[parts[parts.length - 1]!] = value;
  }
  return root;
}

export const uiStringsApi = {
  async fetchPublic(locale: "ru" | "en"): Promise<UiStringsPublicResponse> {
    return apiRequest<UiStringsPublicResponse>(`/ui-strings?locale=${locale}`, {
      public: true,
    });
  },

  async fetchAdmin(): Promise<UiStringsAdminResponse> {
    return apiRequest<UiStringsAdminResponse>("/admin/ui-strings", { audience: "admin" });
  },

  async saveAdmin(items: Array<{ key: string; ru?: string; en?: string }>) {
    return apiRequest<{ ok: boolean; version: string }>("/admin/ui-strings", {
      method: "PUT",
      audience: "admin",
      body: { items },
    });
  },

  /** Load overrides into i18n; uses localStorage cache + version check. */
  async applyToI18n(
    i18n: { addResourceBundle: (lng: string, ns: string, res: object, deep?: boolean, overwrite?: boolean) => void },
    locale: "ru" | "en",
  ): Promise<void> {
    if (!isApiEnabled()) return;
    const cache = readCache();
    try {
      const data = await this.fetchPublic(locale);
      const byLocale = { ...(cache?.byLocale ?? {}) };
      byLocale[locale] = data.strings;
      writeCache({ version: data.version, byLocale });
      if (Object.keys(data.strings).length) {
        i18n.addResourceBundle(locale, "translation", unflattenStrings(data.strings), true, true);
      }
    } catch {
      const cached = cache?.byLocale?.[locale];
      if (cached && Object.keys(cached).length) {
        i18n.addResourceBundle(locale, "translation", unflattenStrings(cached), true, true);
      }
    }
  },
};
